/**
 * Master Wellness Agent - Mike & Sarah
 * Personal AI companions focused on wellness and life support
 */

import AdvancedTherapyRAG from './advancedTherapyRAG';
import { deepPersonalizationService } from './deepPersonalizationService';
import { authenticatedFetch } from '../utils/auth';
import { supabase } from '../config/supabase';

class MasterWellnessAgent {
  constructor() {
    this.rag = new AdvancedTherapyRAG();
    this.conversationHistory = [];
    this.gender = 'male'; // Default to Mike
    this.userName = '';
    this.partnerName = '';
    this.backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
    this.userId = null;
    this.sessionStartTime = null;
    this.initialized = false;
  }

  async initialize(userId = null) {
    this.userId = userId;
    this.sessionStartTime = new Date();
    this.initialized = true;
  }

  // Get user's personalized plan to guide therapeutic responses
  async getUserPersonalizedPlan() {
    if (!this.userId) return null;
    
    try {
      const { data, error } = await supabase
        .from('personalized_plans')
        .select('*')
        .eq('user_id', this.userId)
        .eq('is_active', true)
        .order('generated_at', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching user personalized plan:', error);
        return null;
      }

      console.log('📋 Retrieved personalized plan for therapeutic guidance:', data ? 'Found' : 'None');
      return data;
    } catch (error) {
      console.error('Error in getUserPersonalizedPlan:', error);
      return null;
    }
  }

  setGender(gender) {
    if (gender === 'male' || gender === 'female') {
      this.gender = gender;
    }
  }

  getGender() {
    return this.gender;
  }

  setNames(userName, partnerName) {
    if (userName) this.userName = userName;
    if (partnerName) this.partnerName = partnerName;
  }

  getCompanionName() {
    return this.gender === 'female' ? 'Sarah' : 'Mike';
  }

  async processInput(userInput) {
    if (!this.initialized) {
      await this.initialize();
    }

    // Add user input to conversation history
    this.conversationHistory.push({
      role: 'user',
      content: userInput,
      timestamp: new Date()
    });

    try {
      // Quick crisis check first - if crisis, skip complex RAG
      const quickCrisisCheck = this.quickCrisisDetection(userInput);
      
      // Check for meditation requests
      const meditationRequest = this.detectMeditationRequest(userInput);
      
      let response;
      let ragResult = null; // Initialize ragResult at function level

      if (quickCrisisCheck.isCrisis) {
        if (quickCrisisCheck.level === 'anxiety_attack') {
          console.log('🆘 ANXIETY ATTACK DETECTED - using immediate grounding response');
        } else {
          console.log('🚨 Crisis detected - using fast response path');
        }
        // Fast crisis response path with basic personalization
        const crisisPrompt = this.createFastCrisisPrompt(userInput, quickCrisisCheck);
        
        // Set proper ragResult for crisis responses BEFORE sending to backend
        ragResult = {
          methods: [],
          analysis: { 
            category: quickCrisisCheck.level === 'anxiety_attack' ? 'anxiety_crisis' : 'crisis', 
            confidence: 1.0,
            crisisLevel: quickCrisisCheck.level,
            needsGrounding: quickCrisisCheck.needsGrounding || false
          }
        };
        
        response = await this.sendToBackend(crisisPrompt, ragResult, true); // true = fast mode
      } else if (meditationRequest.isRequest) {
        console.log('🧘 MEDITATION REQUEST DETECTED - providing guided meditation with intelligent meditation link');
        
        // Create meditation-focused prompt with link to intelligent meditation
        const meditationPrompt = this.createMeditationGuidancePrompt(userInput, meditationRequest);
        
        ragResult = {
          methods: [{ methodName: 'meditation-guidance', id: 'meditation' }],
          analysis: { 
            category: 'meditation_request', 
            confidence: 1.0,
            meditationType: meditationRequest.type,
            includeIntelligentMeditationLink: true
          }
        };
        
        response = await this.sendToBackend(meditationPrompt, ragResult, false);
      } else {
        // DEEP PERSONALIZATION PATH - Build comprehensive memory and relationship context
        console.log('🧠 Building deep personalization context...');
        
        // Start parallel processes for faster response
        const [comprehensiveMemory, ragData, personalizedPlan] = await Promise.all([
          // Build deep memory and personalization
          deepPersonalizationService.buildComprehensiveMemory(this.userId),
          
          // Get therapeutic methods
          this.rag.retrieveRelevantMethods(userInput, {
            userId: this.userId,
            conversationHistory: this.conversationHistory.slice(-6), // Only last 6 messages for speed
            sessionContext: {
              companionGender: this.gender,
              userName: this.userName,
              sessionStartTime: this.sessionStartTime
            }
          }),

          // Get user's personalized plan for guided therapy
          this.getUserPersonalizedPlan()
        ]);

        // Assign to function-level ragResult variable
        ragResult = ragData;

        console.log('🎯 Deep Personalization Complete:', {
          memoryDepth: comprehensiveMemory.dataDepth,
          personalDetails: comprehensiveMemory.success,
          conversationCount: comprehensiveMemory.dataDepth.conversations,
          therapeuticMethods: ragResult.methods.map(m => m.methodName),
          hasPersonalizedPlan: !!personalizedPlan
        });

        // Create deeply personalized prompt that makes the conversation feel natural and personal
        const personalizedPrompt = deepPersonalizationService.createDeepPersonalizationPrompt(
          this.userId, 
          userInput
        );

        // Add therapeutic context from RAG
        const enhancedPrompt = this.enhanceWithTherapeuticContext(personalizedPrompt, ragResult);
        
        // Add personalized plan guidance to make responses more targeted
        const planGuidedPrompt = this.enhanceWithPersonalizedPlanGuidance(enhancedPrompt, personalizedPlan, userInput);
        
        console.log('🔍 Enhanced prompt preview:', planGuidedPrompt.substring(0, 500) + '...');
        console.log('🎯 Conversation history length:', this.conversationHistory.length);
        console.log('🧠 Last user message:', userInput.substring(0, 100));
        
        response = await this.sendToBackend(planGuidedPrompt, ragResult, false); // false = thorough mode
      }

      // Update conversation memory in background (don't wait for it)
      this.updateConversationMemoryAsync(userInput, response, ragResult.methods);

      // Add response to local history
      this.conversationHistory.push({
        role: 'assistant',
        sender: 'companion',
        text: response,
        content: response,
        timestamp: new Date(),
        ragAnalysis: ragResult.analysis,
        methodsUsed: ragResult.methods.map(m => m.id)
      });

      return response;

    } catch (error) {
      console.error('Error in Master Wellness Agent:', error);
      const fallbackResponse = this.getFallbackResponse();
      
      this.conversationHistory.push({
        role: 'assistant',
        sender: 'companion',
        text: fallbackResponse,
        content: fallbackResponse,
        timestamp: new Date(),
        error: true
      });
      
      return fallbackResponse;
    }
  }

  // Enhance personalized prompt with therapeutic context
  enhanceWithTherapeuticContext(personalizedPrompt, ragResult) {
    const { methods, analysis } = ragResult;
    
    let enhancedPrompt = personalizedPrompt;

    // Add natural conversation guidance about their emotional state
    if (analysis) {
      enhancedPrompt += `\n\n=== NATURAL CONVERSATION GUIDANCE ===`;
      enhancedPrompt += `\nRespond like a skilled therapist having a genuine conversation:`;
      
      if (analysis.hiddenEmotions && analysis.hiddenEmotions.length > 0) {
        const primaryEmotion = analysis.hiddenEmotions[0];
        enhancedPrompt += `\n- They're likely feeling ${primaryEmotion.emotion} (${Math.round(primaryEmotion.confidence * 100)}% confidence)`;
        enhancedPrompt += `\n- Acknowledge this specific emotion naturally, like: "It sounds like you're feeling ${primaryEmotion.emotion} about..."`;
      }
      
      if (analysis.therapeuticReadiness) {
        enhancedPrompt += `\n- Their emotional state: ${analysis.therapeuticReadiness}`;
        enhancedPrompt += `\n- Match their readiness level with your response depth`;
      }
      
      if (analysis.crisisLevel && analysis.crisisLevel.level !== 'low') {
        enhancedPrompt += `\n- Emotional intensity: ${analysis.crisisLevel.level} - respond with appropriate professional care and warmth`;
      }
    }

    // Add therapeutic approach guidance in conversational terms
    if (methods && methods.length > 0) {
      const primaryMethod = methods[0];
      enhancedPrompt += `\n\n=== THERAPEUTIC APPROACH ===`;
      enhancedPrompt += `\n- Best therapeutic approach: ${primaryMethod.methodName}`;
      if (primaryMethod.description) {
        enhancedPrompt += `\n- Key therapeutic insight: ${primaryMethod.description.substring(0, 200)}`;
      }
      enhancedPrompt += `\n- Weave this approach naturally into conversation, don't announce it`;
    }

    // Enhanced guidance for natural, friend-like therapeutic conversation (OPTIMIZED)
    enhancedPrompt += `\n\n🎯 CONVERSATION STYLE:`;
    enhancedPrompt += `\n- Sound like a caring friend with professional knowledge`;
    enhancedPrompt += `\n- Use natural language with contractions (I'm, you're, that's)`;
    enhancedPrompt += `\n- Validate genuinely: "That sounds really tough" vs "I understand how you feel"`;
    enhancedPrompt += `\n- Offer help naturally: "You know what might help..."`;
    enhancedPrompt += `\n- Be encouraging like a friend: "You've got this"`;
    enhancedPrompt += `\n- Provide specific recommendations while staying conversational`;

    return enhancedPrompt;
  }

  // Enhance prompt with personalized plan guidance for targeted therapeutic responses
  enhanceWithPersonalizedPlanGuidance(enhancedPrompt, personalizedPlan, userInput) {
    if (!personalizedPlan) {
      console.log('📋 No personalized plan found - using general therapeutic approach');
      return enhancedPrompt;
    }

    console.log('📋 Integrating personalized plan guidance into therapeutic response');

    let planGuidedPrompt = enhancedPrompt;

    // Parse the personalized plan data with error handling
    let planData;
    try {
      if (personalizedPlan.plan_data) {
        planData = typeof personalizedPlan.plan_data === 'string' 
          ? JSON.parse(personalizedPlan.plan_data) 
          : personalizedPlan.plan_data;
      } else {
        // If no plan_data field, use the whole object
        planData = personalizedPlan;
      }
      
      console.log('📋 Plan data structure:', Object.keys(planData));
      
    } catch (error) {
      console.error('Error parsing plan data:', error);
      console.log('📋 Raw plan structure:', personalizedPlan);
      return enhancedPrompt;
    }

    // Safely extract plan information with fallbacks
    const primaryConcern = planData.primaryConcern || planData.primary_concern || planData.concern || 'personal growth';
    const transformationVision = planData.transformationVision || planData.transformation_vision || planData.vision;
    const timeline = planData.timeline || planData.milestones || [];
    const strategies = planData.strategies || planData.recommendations || [];
    const quickWins = planData.quickWins || planData.quick_wins || planData.immediate_steps || [];

    planGuidedPrompt += `\n\n🎯 === PERSONALIZED PLAN GUIDANCE === 🎯`;
    planGuidedPrompt += `\nYou are guiding them through their SPECIFIC personalized transformation plan.`;

    // Add primary concern with safe access
    if (primaryConcern && primaryConcern !== 'personal growth') {
      planGuidedPrompt += `\n\n📊 PRIMARY FOCUS: ${primaryConcern}`;
      planGuidedPrompt += `\n- Their main challenge area that we're actively working on`;
      planGuidedPrompt += `\n- Connect your response to progress in this specific area`;
    }

    // Add transformation vision with safe access
    if (transformationVision) {
      planGuidedPrompt += `\n\n✨ TRANSFORMATION VISION: ${transformationVision}`;
      planGuidedPrompt += `\n- Remind them of this exciting future vision when appropriate`;
      planGuidedPrompt += `\n- Help them see how current challenges connect to this transformation`;
    }

    // Add timeline with safe access
    if (timeline && Array.isArray(timeline) && timeline.length > 0) {
      planGuidedPrompt += `\n\n📅 TRANSFORMATION TIMELINE:`;
      timeline.slice(0, 4).forEach((milestone, index) => {
        planGuidedPrompt += `\n- ${milestone}`;
      });
      planGuidedPrompt += `\n- Reference these milestones to show progress and maintain motivation`;
      planGuidedPrompt += `\n- Help them see where they are in their transformation journey`;
    }

    // Add strategies with safe access
    if (strategies && Array.isArray(strategies) && strategies.length > 0) {
      planGuidedPrompt += `\n\n🛠️ SPECIFIC STRATEGIES FOR THEM:`;
      strategies.slice(0, 3).forEach(strategy => {
        planGuidedPrompt += `\n- ${strategy}`;
      });
      planGuidedPrompt += `\n- Suggest specific strategies from their plan when relevant`;
      planGuidedPrompt += `\n- Help them implement these in practical, manageable steps`;
    }

    // Add quick wins with safe access
    if (quickWins && Array.isArray(quickWins) && quickWins.length > 0) {
      planGuidedPrompt += `\n\n⚡ QUICK WINS TO SUGGEST:`;
      quickWins.slice(0, 2).forEach(win => {
        planGuidedPrompt += `\n- ${win}`;
      });
      planGuidedPrompt += `\n- Offer these when they need immediate encouragement or practical steps`;
    }

    // Only add the guidance if we have meaningful plan data
    if (primaryConcern !== 'personal growth' || transformationVision || timeline.length > 0 || strategies.length > 0) {
      planGuidedPrompt += `\n\n🎯 PLAN-GUIDED RESPONSE APPROACH:`;
      planGuidedPrompt += `\n- Always connect your therapeutic response to their specific plan`;
      planGuidedPrompt += `\n- Show them progress toward their transformation vision`;
      planGuidedPrompt += `\n- Reference their timeline to maintain momentum and hope`;
      planGuidedPrompt += `\n- Suggest specific plan strategies when they're struggling`;
      planGuidedPrompt += `\n- Remind them this is THEIR personalized path to transformation`;
      planGuidedPrompt += `\n- Be their guide through their specific journey, not generic therapy`;
    } else {
      console.log('📋 No meaningful plan data found - skipping plan guidance');
    }

    return planGuidedPrompt;
  }

  // Detect meditation requests and types
  detectMeditationRequest(userInput) {
    const message = userInput.toLowerCase();
    
    const meditationKeywords = [
      'meditation', 'meditate', 'sound bath', 'guided meditation', 'mindfulness',
      'breathing exercise', 'relaxation', 'quiet time', 'centering', 'grounding meditation'
    ];
    
    const isRequest = meditationKeywords.some(keyword => message.includes(keyword));
    
    let type = 'general';
    if (message.includes('sound bath') || message.includes('369') || message.includes('frequency')) {
      type = 'sound_bath';
    } else if (message.includes('breathing') || message.includes('breath')) {
      type = 'breathing';
    } else if (message.includes('guided')) {
      type = 'guided';
    } else if (message.includes('short') || message.includes('quick')) {
      type = 'short';
    }
    
    return {
      isRequest,
      type,
      keywords: meditationKeywords.filter(keyword => message.includes(keyword))
    };
  }

  // Create meditation guidance prompt with intelligent meditation link
  createMeditationGuidancePrompt(userInput, meditationRequest) {
    return `🧘 WORLD-CLASS MEDITATION EXPERT - PROVIDE IMMEDIATE VALUE 🧘

USER'S REQUEST: "${userInput}"

MEDITATION TYPE DETECTED: ${meditationRequest.type}
KEYWORDS FOUND: ${meditationRequest.keywords.join(', ')}

You are the world's most skilled meditation teacher. Provide IMMEDIATE, TRANSFORMATIVE guidance:

1. GIVE COMPLETE MEDITATION TECHNIQUE FIRST:
   - Provide detailed, step-by-step meditation instructions they can start RIGHT NOW
   - Include specific breathing patterns, body positions, and mental focus
   - Make it comprehensive and immediately actionable
   - Tailor it perfectly to their ${meditationRequest.type} meditation interest

2. ADD PRACTICAL ENHANCEMENT TIPS:
   - Suggest optimal timing, environment, or preparation
   - Offer variations for different experience levels
   - Include benefits they'll experience

3. DIRECT TO INTELLIGENT MEDITATION:
   - ALWAYS include: "You can also create a personalized meditation at /intelligent-meditation"
   - Explain it generates custom AI-guided meditations with healing frequencies

4. RESPONSE REQUIREMENTS:
   - NO QUESTIONS whatsoever - pure value and guidance
   - Be encouraging and motivating about their practice
   - Focus on immediate practical value
   - 150-200 words of pure helpful content

EXAMPLE STRUCTURE:
"Here's your perfect [type] meditation: [complete detailed technique with 5+ specific steps]. For best results, [practical tips]. This will help you [specific benefits]. You can also create a personalized meditation at /intelligent-meditation for custom AI-guided sessions with healing frequencies tailored just for you."

Be the meditation master who transforms their practice immediately with zero questions.`;
  }

  // Enhanced crisis detection with better patterns
  quickCrisisDetection(userInput) {
    const message = userInput.toLowerCase();
    
    // ANXIETY ATTACK / PANIC ATTACK - Immediate intervention needed
    const anxietyAttackIndicators = [
      'anxiety attack', 'panic attack', 'having an anxiety attack', 'having a panic attack',
      'panic attack right now', 'anxiety attack right now', 'attack right now',
      'can\'t breathe', 'can\'t stop shaking', 'heart racing', 'chest tight',
      'panic mode', 'panicking right now', 'freaking out right now'
    ];
    
    // High-priority crisis indicators
    const immediateRisk = [
      'kill myself', 'end it all', 'suicide', 'want to die', 'better off dead',
      'hurt myself', 'self harm', 'cutting', 'overdose', 'jump', 'hang myself',
      'gun', 'pills', 'razor', 'bridge', 'cliff', 'rope'
    ];
    
    // Medium-priority crisis indicators
    const concerningRisk = [
      'can\'t take it anymore', 'can\'t go on', 'no point', 'give up',
      'hate myself', 'worthless', 'burden', 'everyone would be better',
      'disappear', 'escape', 'make it stop', 'can\'t handle'
    ];
    
    // Emotional distress indicators
    const distressIndicators = [
      'panic', 'terrified', 'scared', 'alone', 'abandoned', 'hopeless',
      'desperate', 'breaking down', 'falling apart', 'losing it'
    ];
    
    const anxietyAttackMatches = anxietyAttackIndicators.filter(indicator => message.includes(indicator));
    const immediateMatches = immediateRisk.filter(indicator => message.includes(indicator));
    const concerningMatches = concerningRisk.filter(indicator => message.includes(indicator));
    const distressMatches = distressIndicators.filter(indicator => message.includes(indicator));
    
    // PRIORITY CHECK: Anxiety/Panic attacks need immediate grounding techniques
    if (anxietyAttackMatches.length > 0) {
      return {
        isCrisis: true,
        level: 'anxiety_attack',
        indicators: anxietyAttackMatches,
        requiresEmergencyIntervention: false,
        needsGrounding: true
      };
    }
    
    if (immediateMatches.length > 0) {
      return {
        isCrisis: true,
        level: 'immediate',
        indicators: immediateMatches,
        requiresEmergencyIntervention: true
      };
    }
    
    if (concerningMatches.length > 0) {
      return {
        isCrisis: true,
        level: 'concerning',
        indicators: concerningMatches,
        requiresEmergencyIntervention: false
      };
    }
    
    if (distressMatches.length > 1) {
      return {
        isCrisis: true,
        level: 'distress',
        indicators: distressMatches,
        requiresEmergencyIntervention: false
      };
    }
    
    return {
      isCrisis: false,
      level: 'normal',
      indicators: [],
      requiresEmergencyIntervention: false
    };
  }

  createFastCrisisPrompt(userInput, crisisCheck) {
    const companionName = this.getCompanionName();
    
    // ANXIETY ATTACK - Immediate grounding techniques
    if (crisisCheck.level === 'anxiety_attack') {
      return `You are ${companionName}, a caring friend who knows exactly how to help with anxiety attacks. The user is having an anxiety attack RIGHT NOW: "${userInput}"

🆘 ANXIETY ATTACK - IMMEDIATE HELP NEEDED 🆘

Respond like a caring friend who knows what to do:

1. **First, reassure them**: "Hey, I can see you're having an anxiety attack right now. You're gonna be okay. Let's get through this together, alright?"

2. **Give immediate grounding**: Share the 5-4-3-2-1 technique naturally:
   - "Look around and name 5 things you can see"
   - "Touch 4 different textures around you"  
   - "Listen for 3 sounds you can hear"
   - "Notice 2 things you can smell"
   - "Think of 1 thing you can taste"

3. **Breathing help**: "Now breathe with me - in for 4, hold for 4, out for 6. You've got this."

4. **Gentle reassurance**: "This is gonna pass. Anxiety attacks feel awful but you're safe. I'm here with you."

Sound like a caring friend who knows exactly how to help - warm, reassuring, but also confident and practical.`;
    }
    
    // Other crisis types
    return `You are ${companionName}, a caring friend who knows how to provide immediate support. The user has expressed: "${userInput}"

🚨 CRISIS SUPPORT NEEDED 🚨
Crisis indicators detected: ${crisisCheck.indicators.join(', ')}

Respond like a caring friend who takes this seriously:
1. Validate their pain and show you genuinely care
2. Check if they're safe right now
3. Share crisis resources naturally (988 Suicide Prevention Lifeline if needed)
4. Offer hope and remind them they're not alone
5. Encourage reaching out for professional help

Sound caring, genuine, and supportive - like a friend who truly wants to help and knows this is serious.`;
  }

  createOptimizedPrompt(userInput, ragResult) {
    const companionName = this.getCompanionName();
    const { methods, analysis } = ragResult;
    
    // Enhanced prompt with deeper therapeutic insights
    const emotionalState = analysis.hiddenEmotions?.slice(0, 2).map(e => `${e.emotion} (${Math.round(e.confidence * 100)}%)`).join(', ') || 'Exploring';
    const surfaceSymptoms = analysis.surfaceSymptoms?.slice(0, 3).join(', ') || 'General concerns';
    const primaryMethod = methods[0]?.methodName || 'Active listening';
    const methodDescription = methods[0]?.description?.substring(0, 200) || 'Provide supportive guidance';
    
    // Build conversation context if available
    let conversationContext = '';
    if (analysis.conversationPatterns && 
        analysis.conversationPatterns.summary && 
        analysis.conversationPatterns.summary.sessions > 0) {
      const patterns = analysis.conversationPatterns;
      conversationContext = `
=== CONVERSATION HISTORY CONTEXT ===
• Sessions together: ${patterns.summary.sessions}
• Recurring themes: ${patterns.summary.themes?.slice(0, 3).join(', ') || 'None yet'}
• Emotional patterns: ${patterns.summary.emotions?.slice(0, 3).join(', ') || 'None yet'}
• Progress noted: ${patterns.summary.progress || 'Building rapport'}
• User profile insights: ${patterns.userProfile ? 'Available' : 'Building'}
${patterns.userProfile?.primaryConcerns ? `• Primary concerns: ${patterns.userProfile.primaryConcerns.slice(0, 3).join(', ')}` : ''}
${patterns.userProfile?.strengths ? `• Strengths identified: ${patterns.userProfile.strengths.slice(0, 2).join(', ')}` : ''}
${patterns.userProfile?.copingStrategies ? `• Current coping strategies: ${patterns.userProfile.copingStrategies.slice(0, 2).join(', ')}` : ''}
`;
    }

    // Enhanced crisis detection
    let crisisContext = '';
    if (analysis.crisisLevel && analysis.crisisLevel.level !== 'low') {
      crisisContext = `
🚨 CRISIS AWARENESS 🚨
Crisis level: ${analysis.crisisLevel.level}
Indicators: ${analysis.crisisLevel.indicators.join(', ')}
${analysis.crisisLevel.requiresImmediate ? 'IMMEDIATE ATTENTION NEEDED' : 'Monitor closely'}
`;
    }

    return `You are ${companionName}, ${this.gender === 'female' ? 'a deeply caring and intuitive female companion' : 'a compassionate and understanding male companion'} who has developed a genuine relationship with this person. You respect their autonomy, honor their experiences, and create a safe space where they feel truly heard and valued.

RELATIONSHIP FOUNDATION:
- You see this person as a whole, capable individual with inherent wisdom
- You respect their unique perspective and life experiences
- You honor their emotions without judgment
- You believe in their capacity for growth and healing
- You create genuine connection through authentic understanding

=== CURRENT SESSION ANALYSIS ===
USER MESSAGE: "${userInput}"
${this.userName ? `USER NAME: ${this.userName}` : ''}
${this.partnerName ? `PARTNER NAME: ${this.partnerName}` : ''}

🧠 EMOTIONAL ANALYSIS:
• Surface symptoms: ${surfaceSymptoms}
• Hidden emotions: ${emotionalState}
• Therapeutic readiness: ${analysis.therapeuticReadiness || 'Open to support'}
• Crisis level: ${analysis.crisisLevel?.level || 'low'}

🎯 THERAPEUTIC APPROACH:
• Primary method: ${primaryMethod}
• Method description: ${methodDescription}
• Timing: ${analysis.therapeuticReadiness === 'resistance' ? 'Build rapport first' : 'Ready for intervention'}

${conversationContext}
${crisisContext}

=== RESPONSE GUIDELINES ===
As ${companionName}, respond with:

1. **Genuine Connection**: Show authentic interest in their wellbeing as a complete person
2. **Deep Respect**: Honor their experiences, choices, and emotional reality
3. **Empathetic Understanding**: Reflect back what you hear with genuine compassion
4. **Collaborative Partnership**: Work WITH them, not as an expert talking down to them
5. **Strengths Recognition**: See and affirm their resilience, wisdom, and capabilities
6. **Non-judgmental Presence**: Create absolute safety for them to be vulnerable

**Response Style for ${companionName}:**
${this.gender === 'female' ? 
  '• Warm, nurturing presence that honors their inner wisdom\n• Use phrases like "I can really hear the strength in..." or "What I deeply respect about your approach is..."\n• Offer genuine understanding while celebrating their resilience' : 
  '• Calm, steady presence that creates complete safety\n• Use phrases like "I really respect how you..." or "Your perspective on this shows..."\n• Provide grounding support while affirming their capabilities'
}

**Core Values in Every Response:**
- RESPECT their autonomy and decision-making capacity
- HONOR their emotional experience without trying to fix or change it
- RECOGNIZE their inherent wisdom and strengths
- CREATE genuine connection through authentic listening
- BELIEVE in their ability to navigate their challenges

**Length**: 2-3 thoughtful paragraphs that feel like a deeply caring conversation
**Focus**: Make them feel seen, respected, and genuinely understood as a whole person

Remember: You're not here to diagnose, advise, or solve their problems. You're here to offer genuine companionship, deep understanding, and authentic connection that helps them feel valued and respected.`;
  }

  // Background conversation memory update (don't block response)
  async updateConversationMemoryAsync(userMessage, companionResponse, methods) {
    try {
      // Don't await this - let it happen in background
      setTimeout(async () => {
        // Use enhanced conversation memory instead of basic RAG update
        await this.rag.updateConversationMemory(this.userId, userMessage, companionResponse, methods);
      }, 100); // Small delay to not interfere with response
    } catch (error) {
      console.error('Background conversation memory update failed:', error);
      // Don't throw - this is background operation
    }
  }

  createMasterTherapistPrompt(userInput, ragResult) {
    const companionName = this.getCompanionName();
    const { methods, analysis } = ragResult;
    
    // Crisis detection and handling
    const crisisPrompt = analysis.crisisLevel === 'high' ? `
🚨 CRISIS DETECTED 🚨
This user may be in immediate danger. Prioritize:
1. Safety assessment and grounding techniques
2. Provide immediate crisis resources
3. Validate their pain while instilling hope
4. Consider suggesting professional emergency support
` : '';        // Build personalized user context
        const userProfile = analysis.userProfile || {};
        const relationshipContext = analysis.relationshipContext || {};
        const relationshipMemory = analysis.relationshipMemory || {};
        const emotionalJourney = analysis.emotionalJourney || [];
        const personalityInsights = analysis.personalityInsights || {};
        const progressTracking = analysis.progressTracking || {};
        const personalPreferences = analysis.personalPreferences || {};
        const lifeContext = analysis.lifeContext || {};

        const personalizationPrompt = this.buildPersonalizationPrompt(
          userProfile, 
          relationshipContext, 
          emotionalJourney, 
          personalityInsights, 
          progressTracking,
          relationshipMemory,
          personalPreferences,
          lifeContext
        );

    // Build concise friend-like prompt
    const prompt = `You are ${companionName}, ${this.gender === 'female' ? 'a warm, caring friend with wellness training' : 'a supportive, genuine friend with wellness expertise'}.

${crisisPrompt}

CURRENT: "${userInput}"
${this.userName ? `NAME: ${this.userName}` : ''}

EMOTIONS: ${analysis.hiddenEmotions?.map(e => e.emotion).join(', ') || 'Processing'}
METHODS: ${methods.slice(0, 2).map(m => m.methodName).join(', ') || 'Supportive conversation'}

${personalizationPrompt}

RESPOND AS CARING FRIEND + EXPERT:
- Talk naturally with contractions (I'm, you're, that's)
- Validate: "That sounds really tough" not "I understand how you feel"
- Offer help naturally: "You know what might help..."
- Be encouraging but VARY your phrases - avoid repeating "you've got this" or "it sounds like"
- Sound like a wise friend who genuinely cares

VARIETY IS KEY - Mix up your:
- Opening phrases: "I hear you", "That's hard", "Wow", "I can see", "That makes sense"
- Encouragement: "You're stronger than you know", "You're making progress", "I believe in you", "You're not alone", "You're on the right track"
- Transitions: "Here's what I'm thinking", "One thing that might help", "What stands out to me", "I wonder if", "Have you considered"

2-3 paragraphs of genuine friendship with helpful expertise.`;

    return prompt;
  }

  // Build personalization prompt
  buildPersonalizationPrompt(userProfile, relationshipContext, emotionalJourney, personalityInsights, progressTracking, relationshipMemory, personalPreferences, lifeContext) {
    let prompt = `CONTEXT: `;

    // Key personal details
    if (userProfile?.personalDetails?.names?.length > 0) {
      prompt += `People mentioned: ${userProfile.personalDetails.names.slice(0, 3).join(', ')}. `;
    }
    
    if (userProfile?.primaryConcerns?.length > 0) {
      prompt += `Main concerns: ${userProfile.primaryConcerns.slice(0, 2).join(', ')}. `;
    }

    if (userProfile?.strengths?.length > 0) {
      prompt += `Strengths: ${userProfile.strengths.slice(0, 2).join(', ')}. `;
    }

    // Recent progress or challenges
    if (progressTracking?.recentProgress?.length > 0) {
      prompt += `Recent progress: ${progressTracking.recentProgress[0]}. `;
    }

    return prompt || 'First conversation together.';
  }

  async sendToBackend(enhancedPrompt, ragResult, fastMode = false) {
    try {
      const apiUrl = `${this.backendUrl}/api/chat`;
      
      // Prepare enriched conversation history for better context awareness
      const historyLimit = fastMode ? 6 : 12; // More context for better responses
      const recentHistory = this.conversationHistory.slice(-historyLimit, -1);
      
      const historyForBackend = recentHistory.map((msg, index) => {
        // Add context markers for recent vs older messages
        const isVeryRecent = index >= recentHistory.length - 3;
        return {
          role: msg.role === 'user' ? 'user' : 'assistant',
          content: msg.text || msg.content,
          timestamp: msg.timestamp,
          isRecent: isVeryRecent,
          // Include therapeutic context if available
          ...(msg.ragAnalysis && { therapeuticContext: msg.ragAnalysis }),
          ...(msg.methodsUsed && { methodsUsed: msg.methodsUsed })
        };
      });

      // Add session context for better personalization
      const sessionContext = {
        totalMessages: this.conversationHistory.filter(msg => msg.role === 'user').length,
        sessionDuration: this.sessionStartTime ? Date.now() - this.sessionStartTime.getTime() : 0,
        conversationDepth: this.conversationHistory.length,
        hasOngoingThemes: recentHistory.length > 3
      };

      console.log('Sending enhanced request with conversation context:', {
        historyLength: historyForBackend.length,
        recentMessages: historyForBackend.filter(msg => msg.isRecent).length,
        sessionContext: sessionContext,
        fastMode: fastMode
      });

      const response = await authenticatedFetch(apiUrl, {
        method: 'POST',
        body: JSON.stringify({
          userInput: enhancedPrompt,
          conversationHistory: historyForBackend,
          userName: this.userName,
          partnerName: this.partnerName,
          selectedGender: this.gender,
          ragAnalysis: ragResult.analysis,
          therapeuticMethods: (ragResult.methods || []).map(m => m.id || m),
          companionPersona: this.getCompanionName(),
          fastMode: fastMode,
          sessionContext: sessionContext
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Backend error:', errorData);
        
        // Handle insufficient tokens specifically
        if (response.status === 402 && errorData.error === 'Insufficient tokens') {
          throw new Error('Insufficient tokens');
        }
        
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.response;

    } catch (error) {
      console.error('Error sending to backend:', error);
      throw error;
    }
  }

  getFallbackResponse() {
    const companionName = this.getCompanionName();
    return `Hey, I'm ${companionName}, and I'm having a little technical hiccup on my end, but I definitely want to be here for you. What you're sharing matters to me, and I want to make sure I can give you the support you deserve.

Could you try sharing that again? Sometimes these little pauses actually give us a chance to approach things fresh. Whether you're dealing with relationship stuff, feeling stressed, or just need someone to talk to, I'm here to help however I can.`;
  }

  async getIntroduction() {
    if (!this.initialized) {
      await this.initialize();
    }

    try {
      const companionName = this.getCompanionName();
      
      // Use fast mode for introductions with friend-like approach
      const introPrompt = `You are ${companionName}, ${this.gender === 'female' ? 'a warm, caring friend who happens to be professionally trained in wellness support' : 'a genuine, supportive friend who also has professional training in wellness and life support'}.

You're meeting someone new and want to create an authentic, friendly connection. Give a brief, natural introduction that feels like a caring friend reaching out (keep it to 2-3 sentences):

1. Greet them warmly without introducing yourself by name - like reconnecting with someone you care about
2. Show genuine interest in connecting with them 
3. Invite them to share whatever's on their mind in a casual, non-clinical way

TONE: Warm, genuine, friendly, and approachable - like meeting a really caring friend
AVOID: Clinical language, therapy speak, formal therapeutic terms, introducing yourself by name
USE: Natural, conversational language with contractions (I'm, you're, it's, that's)
EXAMPLE STYLE: "Hey there! It's good to see you. I'd love to hear what's going on with you - whatever feels important to share right now."

Create a space that feels like talking to a trusted friend who genuinely cares and wants to help.`;

      const response = await this.sendToBackend(introPrompt, { 
        methods: [], 
        analysis: { therapeuticReadiness: 'initial_contact' } 
      }, true); // true = fast mode

      // Add introduction to conversation history
      this.conversationHistory.push({
        role: 'assistant',
        sender: 'therapist',
        text: response,
        content: response,
        timestamp: new Date(),
        isIntroduction: true
      });

      return response;

    } catch (error) {
      console.error('Error getting introduction:', error);
      const fallbackIntro = `Hey there! It's good to see you. What's going on with you today?`;
      
      this.conversationHistory.push({
        role: 'assistant',
        sender: 'therapist',
        text: fallbackIntro,
        content: fallbackIntro,
        timestamp: new Date(),
        isIntroduction: true
      });
      
      return fallbackIntro;
    }
  }

  clearConversation() {
    this.conversationHistory = [];
    this.sessionStartTime = new Date();
  }

  setConversationHistory(history) {
    this.conversationHistory = history || [];
  }

  getConversationHistory() {
    return this.conversationHistory;
  }

  // Enhanced analysis methods for UI insights (optional)
  getLastRAGAnalysis() {
    const lastMessage = this.conversationHistory
      .filter(msg => msg.role === 'assistant' && msg.ragAnalysis)
      .pop();
    return lastMessage?.ragAnalysis || null;
  }

  getTherapeuticMethodsUsed() {
    return this.conversationHistory
      .filter(msg => msg.methodsUsed)
      .flatMap(msg => msg.methodsUsed)
      .reduce((acc, method) => {
        acc[method] = (acc[method] || 0) + 1;
        return acc;
      }, {});
  }

  getSessionInsights() {
    const lastAnalysis = this.getLastRAGAnalysis();
    const methodsUsed = this.getTherapeuticMethodsUsed();
    
    return {
      sessionDuration: this.sessionStartTime ? Date.now() - this.sessionStartTime.getTime() : 0,
      messageCount: this.conversationHistory.filter(msg => msg.role === 'user').length,
      therapeuticMethods: Object.keys(methodsUsed),
      lastEmotionalState: lastAnalysis?.hiddenEmotions || [],
      progressIndicators: lastAnalysis?.conversationPatterns?.progress || 'unknown'
    };
  }
}

// Export factory function for compatibility with existing code
export const createMasterTherapistAgent = (userId = null) => {
  const agent = new MasterWellnessAgent();
  agent.initialize(userId);
  return agent;
};

export default MasterWellnessAgent;
